import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ArrowRightIcon, Body2, Button, Dialog, Input, InputPassword, Label } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import { Map } from "immutable";
import PropTypes from "prop-types";
import passwordStrengthEstimator from "zxcvbn";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { usePasswordPolicy } from "~/components/Hooks/usePasswordPolicy";
import { PasswordPolicyInfo } from "~/components/PasswordPolicyInfo/PasswordPolicyInfo";
import SetPasswordDialog from "~/pages/Onboarding/SetPasswordDialog";
import { SUBMIT_BASIC_DETAILS_ONBOARDING_SUCCESS } from "~/store/action_types";
import { submitBasicDetailsOnboarding } from "~/store/actions/actions.users";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";

export const BasicDetailsOnboarding = ({ afterSubmit }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { passwordPolicyValidation, setMinLength, setRequireNumbers, setRequireMixedCase, setRequireSymbols } =
    usePasswordPolicy();
  const dispatch = useDispatch();
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();
  const passwordPolicy = useSelector((state) => getRawPersonalizedConfigOption(state, "password_policy"));

  const getPasswordStrength = useCallback(
    (password) => {
      if (passwordPolicyValidation(password).valid) {
        const score = passwordStrengthEstimator(password?.trim()).score;
        return score < 1 ? 1 : score;
      }
      return 0;
    },
    [passwordPolicyValidation]
  );

  const resolvedLevels = useCallback((errors) => {
    return [errors ? "Invalid password" : "Strong and memorable", "Weak", "Okay", "Good", "Strong"];
  }, []);

  const handleSubmit = useCallback(
    (values) => {
      setIsLoading(true);
      dispatch(submitBasicDetailsOnboarding(values)).then((result) => {
        setIsLoading(false);
        if (result?.type === SUBMIT_BASIC_DETAILS_ONBOARDING_SUCCESS) {
          afterSubmit(2);
        }
      });
    },
    [afterSubmit, dispatch]
  );

  useEffect(() => {
    if (Map.isMap(passwordPolicy)) {
      const {
        min_length = 5,
        require_mixed_case = false,
        require_numbers = false,
        require_symbols = false,
      } = passwordPolicy.toJS();

      setMinLength(min_length);
      setRequireNumbers(require_numbers);
      setRequireMixedCase(require_mixed_case);
      setRequireSymbols(require_symbols);
    }
  }, [passwordPolicy, setMinLength, setRequireMixedCase, setRequireNumbers, setRequireSymbols]);

  return (
    <Formik
      initialValues={{ first_name: "", last_name: "", password: "" }}
      validate={(values) => {
        const errors = {};

        if (!values?.first_name || values?.first_name?.length > 50) {
          errors.first_name = "First name must be valid.";
        }

        if (!values?.last_name || values?.last_name?.length > 50) {
          errors.last_name = "Last name must be valid.";
        }

        const result = passwordPolicyValidation(values.password);
        if (!result.valid) {
          errors.password = result.errors;
        }

        return errors;
      }}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isValid, submitForm, errors, touched }) => {
        return (
          <>
            <SetPasswordDialog
              isValid={isValid}
              title={t("Basic Information")}
            >
              <Form className="tw-flex tw-flex-col tw-gap-6">
                <Body2 color="secondary">
                  {t("Based on the information you share, we will customize your ActiveCollab workspace")}.
                </Body2>
                <Dialog.ContentDivider />
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-grow-0 tw-gap-2">
                  <Field name="first_name">
                    {({ field }) => (
                      <div className="tw-flex tw-flex-col tw-w-full">
                        <Label
                          htmlFor="first_name"
                          className="tw-mb-1"
                          variant="Caption 1"
                          invalid={touched.first_name && errors.first_name}
                        >
                          {t("First Name")}*
                        </Label>
                        <Input
                          id="first_name"
                          data-testid="first_name"
                          className="tw-w-full tw-shrink-0"
                          value={field.value}
                          onChange={field.onChange}
                          maxLength={50}
                          invalid={touched.first_name && errors.first_name}
                          ref={(el) => (fieldRef.current.first_name = el)}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="last_name">
                    {({ field }) => (
                      <div className="tw-flex tw-flex-col tw-w-full">
                        <Label
                          htmlFor="last_name"
                          className="tw-mb-1"
                          variant="Caption 1"
                          invalid={touched.last_name && errors.last_name}
                        >
                          {t("Last Name")}*
                        </Label>
                        <Input
                          id="last_name"
                          data-testid="last_name"
                          className="tw-w-full tw-shrink-0"
                          value={field.value}
                          onChange={field.onChange}
                          maxLength={50}
                          invalid={touched.last_name && errors.last_name}
                          ref={(el) => (fieldRef.current.last_name = el)}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <Field name="password">
                  {({ field, form, meta }) => (
                    <div className="tw-w-full tw-flex tw-flex-col">
                      <Label
                        htmlFor="password"
                        className="tw-mb-1"
                        variant="Caption 1"
                        invalid={touched.password && errors.password}
                      >
                        {t("Password")}*
                      </Label>
                      <InputPassword
                        id="password"
                        className="tw-w-[300px] sm:tw-w-[480px]"
                        value={field.value}
                        onChange={(v) => form.setFieldValue(field.name, v)}
                        validation={{
                          levels: resolvedLevels(form.errors?.password),
                          getStrength: getPasswordStrength,
                        }}
                        errorMessage={meta.error ? <PasswordPolicyInfo errors={meta.error} /> : null}
                        invalid={touched.password && errors.password}
                        ref={(el) => (fieldRef.current.password = el)}
                      />
                    </div>
                  )}
                </Field>
              </Form>
            </SetPasswordDialog>
            <Dialog.Actions>
              <Button
                disabled={isLoading}
                onClick={() => {
                  focusFirstInvalidField(errors, false);
                  submitForm();
                }}
              >
                {t(`Next`)} <ArrowRightIcon />
              </Button>
            </Dialog.Actions>
          </>
        );
      }}
    </Formik>
  );
};

BasicDetailsOnboarding.propTypes = {
  afterSubmit: PropTypes.func,
};
